import { env } from './env';
import { resolveGameplayHistoryReplayEnabled } from './gameplayHistoryReplayFlag';

export const mockedAuthToken =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJqb2lucmFkYXIuY29tIiwiZXhwIjoxNjgxMzcxNzAyLCJ1c2VySWQiOiI3OGEzYzgwZS01ODc1LTQwZTMtYTA0My1jMjA2OGE1NmVmYzciLCJpYXQiOjE1OTQ5NzE3MDJ9.4yVXDP_hyDbVKsD1X3OCbLDuocnvpDgmV8Ow931JpOg';

// API base URL: must point to the backend server (e.g. https://api.turfmaster.localhost:1355). Do not use the dev server port or /login and /register will 404.
const apiUrl = (() => {
  const raw = env.api;
  // Local dev: if the app runs on `localhost`, prefer hitting the API on `localhost`
  // as well so the HttpOnly refresh cookie is same-site and can be sent on `/auth/refresh`.
  // Using `api.turfmaster.localhost` while the frontend is `localhost` makes the cookie cross-site.
  if (typeof window !== 'undefined' && env.dev && window.location.hostname === 'localhost') {
    if (typeof raw === 'string' && raw.includes('api.turfmaster.localhost')) {
      const rewritten = raw.replace('api.turfmaster.localhost', 'localhost');

      return rewritten;
    }
  }
  return raw;
})();
// Site base URL for share/invitation links. Fallback to current origin when VITE_URL is unset (e.g. E2E, local dev).
const siteUrl = env.url ?? (typeof window !== 'undefined' ? window.location.origin : '');

if (typeof window !== 'undefined' && env.dev) {
  const u = apiUrl ?? '';
  if (!u || u.includes('localhost:8000')) {
    console.warn(
      '[Turfmaster] API requests are going to the wrong host (missing or port 8000). ' +
        'Set VITE_API=https://api.turfmaster.localhost:1355 in the client .env and restart the dev server so /login and /register hit the backend.',
    );
  }
}

// Cloudflare Turnstile configuration (client-side only)
const turnstileSiteKey = env.turnstileSiteKey;

// Use dummy keys for development/testing environments. In E2E, disable Turnstile entirely
// so automated tests are not blocked by rate limits ("Zu viele Versuche...") or CAPTCHA.
const getTurnstileSiteKey = () => {
  if (env.e2eTest === 'true') {
    return undefined;
  }
  if (turnstileSiteKey) {
    return turnstileSiteKey;
  }
  if (env.dev || env.mode === 'local' || env.mode === 'testing' || env.mode === 'test') {
    return '1x00000000000000000000AA';
  }
  return undefined;
};

const discordInviteUrl = env.discordInviteUrl;
const whatsappInviteUrl = env.whatsappInviteUrl;

export const DEBUG_logWhyDidYouRender = false;

export const config = {
  baseUrl: apiUrl,
  siteUrl,

  // log every http request and response object via Pino (apiLogger)
  apiLogEnabled: true,

  reduxLogEnabled: false,

  // enable / disable mock api per entity. disabled means requests will actually call the remote api
  useMockedAuthToken: false,
  accountApiMockEnabled: false,
  authApiMockEnabled: false,
  horsesApiMockEnabled: false,
  usersApiMockEnabled: false,
  toplistsApiMockEnabled: false,
  gamesApiMockEnabled: false,

  mockApiFallThroughRemote: true,

  // debug configuration
  enableMarkerView: false,

  // Cloudflare Turnstile: enabled when a site key is available. Disabled in E2E to avoid rate limits.
  turnstileSiteKey: getTurnstileSiteKey(),
  turnstileEnabled: !!getTurnstileSiteKey(),
  isLocalEnvironment: false, // Set after environment is defined

  // Discord community configuration (deprecated - moved to WhatsApp)
  discordInviteUrl: discordInviteUrl,
  discordEnabled: !!discordInviteUrl, // Auto-enable if invite URL is provided

  // WhatsApp community configuration
  whatsappInviteUrl: whatsappInviteUrl,
  whatsappEnabled: !!whatsappInviteUrl, // Auto-enable if invite URL is provided

  // Beta/preview hints: badge and prerelease text on start page. Set VITE_SHOW_BETA_HINT=false when leaving beta.
  showBetaHint: env.showBetaHint !== 'false',

  // Gameplay replay/history feature gate: defaults dev-on, prod-off (override via VITE_ENABLE_GAMEPLAY_HISTORY_REPLAY).
  gameplayHistoryReplayEnabled: resolveGameplayHistoryReplayEnabled(
    env.enableGameplayHistoryReplay,
    env.dev,
  ),

  // setup configuration
  freeGamePullInterval: 10 * 1000, // in ms
  tournamentHorseCount: 2,
  tournamentPlayerCount: 3,
  tournamentPullInterval: 10 * 1000, // in ms
  tournamentNextInterval: 12 * 60 * 60 * 1000, // in ms
};

export const gameActionWatchdogTimeout = 30 * 1000; // in ms

/**
 * Enables debug menu and React Query DevTools in development.
 * Does NOT depend on frontend port; uses NODE_ENV and api base URL only.
 */
const isLocalhostLikeUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    return (
      hostname === 'localhost' ||
      hostname.endsWith('.localhost') ||
      hostname === '127.0.0.1' ||
      hostname === '::1'
    );
  } catch {
    return url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
  }
};

export const DEBUG_enabled =
  env.dev || isLocalhostLikeUrl(apiUrl) || apiUrl?.includes('api-testing.turfmaster.de');

export enum Environments {
  LOCAL = 'local',
  TESTING = 'testing',
  STAGING = 'staging',
  PRODUCTION = 'production',
  UNKNOWN = 'unknown',
}

export const environment: Environments = (() => {
  const resolvedEnvironment = env.mode;

  switch (resolvedEnvironment) {
    case 'local':
    case 'development':
      return Environments.LOCAL;
    case 'test':
    case 'testing':
      return Environments.TESTING;
    case 'staging':
      return Environments.STAGING;
    case 'production':
      return Environments.PRODUCTION;
    default:
      if (env.dev) {
        return Environments.LOCAL;
      }
      if (env.prod) {
        return Environments.PRODUCTION;
      }
      return Environments.UNKNOWN;
  }
})();

export const isLocalEnvironment = environment === Environments.LOCAL;

// Add isLocalEnvironment to config object after environment is defined
config.isLocalEnvironment = isLocalEnvironment;
export const isTestingEnvironment = environment === Environments.TESTING;
export const isStagingEnvironment = environment === Environments.STAGING;
export const isProductionEnvironment = environment === Environments.PRODUCTION;
export const isUnknownEnvironment = environment === Environments.UNKNOWN;
export const isAnalyticsEnabled = isProductionEnvironment;

export const isGameplayHistoryReplayEnabled = config.gameplayHistoryReplayEnabled;

/**
 * Tutorial and help links configuration
 * Update these URLs with the actual links for your TurfMaster game
 */
export const tutorialConfig = {
  // Rules page link (same for all languages)
  instructionsPdf: {
    de: 'https://aza-spiele.de/turfmaster/regeln/',
    en: 'https://aza-spiele.de/turfmaster/regeln/',
    es: 'https://aza-spiele.de/turfmaster/regeln/',
  },

  // YouTube channel link
  youtubeChannel: 'https://www.youtube.com/@AZA-Spiele',

  // Creator homepage links by language
  creatorHomepage: {
    de: 'https://www.aza-spiele.de',
    en: 'https://www.aza-spiele.de',
    es: 'https://www.aza-spiele.de',
  },

  // AGBs (Terms and Conditions) - external URL (production)
  agbs: {
    de: 'https://aza-spiele.de/turfmaster-online-agb/',
    en: 'https://aza-spiele.de/turfmaster-online-agb/',
    es: 'https://aza-spiele.de/turfmaster-online-agb/',
  },

  // Privacy Policy - external URL (production)
  privacy: {
    de: 'https://aza-spiele.de/turfmaster-online-datenschutz/',
    en: 'https://aza-spiele.de/turfmaster-online-datenschutz/',
    es: 'https://aza-spiele.de/turfmaster-online-datenschutz/',
  },

  // Impressum (single URL; re-exported as ROUTE_IMPRESSUM in routes)
  impressum: 'https://aza-spiele.de/impressum/',

  // Alternative: You can also use relative paths for local files
  // instructionsPdf: '/assets/turfmaster-instructions.pdf',
};

export default config;
